import "./InGameLoginView.css"
import Navbar from "../../component/navbar/Navbar";
import Title from "../../component/title/Title";
import ViewTitle from "../../component/title/view/ViewTitle";
import Text from "../../component/text/Text";
import Footer from "../../component/footer/Footer";
import fetchWithRetry from "../../util/request";
import {useEffect, useMemo, useState} from "react";
import Button from "../../component/button/Button";
import {useDispatch} from "react-redux";
import {login, useAuth} from "../../app/auth/auth";
import {useNavigate} from "react-router-dom";
import {useAppSelector} from "../../app/hooks";
import {useTranslation} from "react-i18next";

export default function InGameLoginView() {
    const {t} = useTranslation();
    const query = useMemo(() => new URLSearchParams(window.location.search), []);
    const navigate = useNavigate();

    const user = useAppSelector(useAuth).user;
    const dispatch = useDispatch();

    // Already signed in -> leave.
    useEffect(() => {
        if (user) navigate(query.get("return") ?? "/")
    }, [user, query, navigate])

    const [code, setCode] = useState<string | null>(null);
    const [expired, setExpired] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    // When no code was supplied, ask the backend for a fresh one.
    useEffect(() => {
        if (code) return;
        let cancelled = false;
        fetch(process.env.REACT_APP_API_BASE + "/auth/ingame/generate", {method: "POST"})
            .then(res => res.json())
            .then(body => {
                if (!cancelled) setCode(body.code)
            })
            .catch(() => {
                if (!cancelled) setError(t("ingameLogin.generateError"))
            })
        return () => {
            cancelled = true
        }
    }, [code, t])

    // Poll the backend until the player approves the code in-game.
    useEffect(() => {
        if (!code) return;
        setExpired(false);
        return fetchWithRetry(res => {
            if (res.status === 404) {
                setExpired(true);
                return true; // code expired or never existed -> stop polling
            }
            if (!res.ok) return false;
            return res.json().then(body => {
                if (body.status === "CONFIRMED" && body.token) {
                    dispatch(login(body.token));
                    return true;
                }
                return false;
            })
        }, process.env.REACT_APP_API_BASE + "/auth/ingame/" + code, {}, 2000)
    }, [code, dispatch])

    function regenerate() {
        setExpired(false);
        setError(null);
        setCopied(false);
        setCode(null); // triggers the generate effect again
    }

    const command = code ? `/web ${code}` : "";

    function copyCommand() {
        if (!command) return;
        navigator.clipboard?.writeText(command).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => {
        });
    }

    let content;
    if (error) {
        content = <>
            <Text color={"red"}>{error}</Text>
            <Button onClick={regenerate}>{t("ingameLogin.tryAgain")}</Button>
        </>
    } else if (expired) {
        content = <>
            <Text color={"red"}>{t("ingameLogin.expired")}</Text>
            <Button onClick={regenerate}>{t("ingameLogin.generateNew")}</Button>
        </>
    } else if (!code) {
        content = <>
            <div className={"loader big"}/>
            <Text>{t("ingameLogin.generatingCode")}</Text>
        </>
    } else {
        content = <>
            <Text spacing={true}>{t("ingameLogin.connectPrompt")}</Text>
            <div className={"code-command"} onClick={copyCommand} title={t("ingameLogin.clickToCopyTitle") as string}>
                {command}
            </div>
            <Text>{copied ? t("common.copied") : t("ingameLogin.clickCommandToCopy")}</Text>
            <div className={"waiting"}>
                <div className={"loader"}/>
                <Text>{t("ingameLogin.waitingConfirmation")}</Text>
            </div>
        </>
    }

    return <div className={"ingame-login-view"}>
        <Navbar/>
        <ViewTitle>
            <Title>{t("ingameLogin.title")}</Title>
            <Text>{t("ingameLogin.subtitle")}</Text>
        </ViewTitle>
        <div className={"ingame-login-container small-shadow"}>
            {content}
        </div>
        <Footer/>
    </div>
}
